import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import useAuth from '../../../auth/useAuth';
import usersApi from '../../../api/usersApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import UserFormDialog from '../../../sections/admin/users/UserFormDialog';

const roleColors = {
  administrador: 'error',
  tecnico: 'info',
  cliente: 'default',
};

export default function UsersListPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useAuth();

  const [roles, setRoles] = useState([]);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce del buscador para no disparar una peticion en cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const { data } = await usersApi.getRoles();
        setRoles(data.data);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar los roles.'), { variant: 'error' });
      }
    };

    loadRoles();
  }, [enqueueSnackbar]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await usersApi.getUsers({
        search: search || undefined,
        role_id: roleFilter || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de usuarios.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleNew = () => {
    setEditingUser(null);
    setFormOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormOpen(true);
  };

  const handleFormSaved = () => {
    setFormOpen(false);
    fetchUsers();
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await usersApi.deleteUser(userToDelete.id);
      enqueueSnackbar('Usuario eliminado correctamente.', { variant: 'success' });
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar el usuario.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Nombre', flex: 1, minWidth: 200 },
      { field: 'email', headerName: 'Correo', flex: 1, minWidth: 220 },
      { field: 'phone', headerName: 'Teléfono', width: 140, valueGetter: (value) => value || '—' },
      {
        field: 'role',
        headerName: 'Rol',
        width: 150,
        renderCell: (params) => (
          <Chip label={params.value || 'Sin rol'} color={roleColors[params.value] || 'default'} size="small" />
        ),
      },
      {
        field: 'created_at',
        headerName: 'Registrado',
        width: 130,
        valueFormatter: (value) => (value ? new Date(value).toLocaleDateString('es-PE') : '—'),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Editar"
            onClick={() => handleEdit(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            disabled={params.row.id === currentUser?.id}
            onClick={() => setUserToDelete(params.row)}
          />,
        ],
      },
    ],
    [currentUser]
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }} direction="row">
        <div>
          <Typography variant="h4">Usuarios y roles</Typography>
          <Typography variant="body2" color="text.secondary">
            Listado de usuarios y asignación de rol (cliente / administrador / técnico).
          </Typography>
        </div>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nuevo usuario
        </Button>
      </Stack>

      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 5 }}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="Nombre o correo"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              label="Rol"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
        />
      </Card>

      <UserFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={handleFormSaved}
        user={editingUser}
        roles={roles}
      />

      <Dialog open={Boolean(userToDelete)} onClose={() => setUserToDelete(null)}>
        <DialogTitle>Eliminar usuario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar a <strong>{userToDelete?.name}</strong>? Esta acción se puede revertir solo
            desde la base de datos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
